import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import Modal from "bootstrap/js/dist/modal";
import styled from "styled-components";
import { API_BASE_URL } from "../../../environment";
import { AuthContext } from "../../../context/authcontext";
import CollapseHeader from "../../../core/common/collapse-header";
import { Link } from "react-router-dom";
import SettingsTabsNavigation from "../../../components/Tabs/SettingsTabsNavigation";
import { useTenantRole } from "../../../components/TenantRoleDisplay";
import CompanyLogo from "../../../assets/company.png";

const MySwal = withReactContent(Swal);

const TableContainer = styled.div`
  max-height: calc(10 * 65px);
  min-height: calc(10 * 65px);
  overflow-y: auto;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(60, 60, 60, 0.07);
  background: #fff;
  border: 1px solid #e3e7ef;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: #fff;
  thead th {
    background: #f4f6fa;
    color: #222;
    font-weight: 600;
    border-bottom: 2px solid #e3e7ef;
    padding: 0.85rem 0.75rem;
    vertical-align: middle;
    font-size: 1rem;
    position: sticky;
    top: 0;
    z-index: 1;
  }
  tbody tr {
    transition: background 0.15s;
  }
  tbody tr:nth-child(even) {
    background: #f9fafc;
  }
  tbody tr:hover {
    background: #f1f5fa;
  }
  td {
    padding: 0.75rem;
    vertical-align: middle;
    border-bottom: 1px solid #e3e7ef;
    font-size: 0.98rem;
  }
`;

const StyledCheckbox = styled.input.attrs({ type: "checkbox" })`
  accent-color: #0d6efd;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1.5px solid #b0b5c3;
  background: #fff;
  transition: box-shadow 0.15s, border-color 0.15s;
  box-shadow: 0 1px 2px rgba(60, 60, 60, 0.04);
  margin: 0;
  &:focus {
    outline: none;
    border-color: #0d6efd;
    box-shadow: 0 0 0 2px #e3e7ef;
  }
`;

const ResponsiveFormSection = styled.div`
  @media (max-width: 768px) {
    .col-md-6,
    .col-md-4 {
      margin-bottom: 1rem !important;
    }
    .modal-lg {
      max-width: 95vw !important;
      margin: 0.5rem;
    }
    .card-body {
      padding: 1rem 0.75rem;
    }
    .form-section-title {
      font-size: 0.95rem;
    }
  }
  @media (max-width: 576px) {
    .modal-lg {
      max-width: 98vw !important;
      margin: 0.25rem;
    }
  }
`;

interface Tenant {
  tenant_id: number;
  tenant_name: string;
  domain_name: string;
  is_active: boolean;
  created_date: string;
  tenant_logo?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  country?: string;
  zip_code?: string;
  is_service_tax?: boolean;
  is_gst_tax?: boolean;
  is_vat_tax?: boolean;
  service_tax_default_percentage?: number;
  gst_tax_default_percentage?: number;
  vat_tax_default_percentage?: number;
}

export default function TenantManagement() {
  const { auth, setAuth, selectedTenantId } = useContext(AuthContext);
  const userRole = useTenantRole(selectedTenantId || 0);

  const [batchPage, setBatchPage] = useState(1);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [uiPage, setUiPage] = useState(1);
  const rowsPerPage = 12;

  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [editTenant, setEditTenant] = useState<Tenant | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [addTenant, setAddTenant] = useState({
    tenant_name: "",
    domain_name: "",
    is_active: true,
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    country: "",
    zip_code: "",
    is_service_tax: false,
    is_gst_tax: false,
    is_vat_tax: false,
    service_tax_default_percentage: 0,
    gst_tax_default_percentage: 0,
    vat_tax_default_percentage: 0,
  });
  const [isAdding, setIsAdding] = useState(false);

  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  const [addDomainError, setAddDomainError] = useState("");

  const [editLogoFile, setEditLogoFile] = useState<File | null>(null);
  const [editLogoPreview, setEditLogoPreview] = useState<string>("");
  const [addLogoFile, setAddLogoFile] = useState<File | null>(null);
  const [addLogoPreview, setAddLogoPreview] = useState<string>("");

  const [editServiceTaxError, setEditServiceTaxError] = useState("");
  const [editGstTaxError, setEditGstTaxError] = useState("");
  const [editVatTaxError, setEditVatTaxError] = useState("");
  const [addServiceTaxError, setAddServiceTaxError] = useState("");
  const [addGstTaxError, setAddGstTaxError] = useState("");
  const [addVatTaxError, setAddVatTaxError] = useState("");

  const loadTenants = useCallback(async (page: number, search: string = "") => {
    setIsLoading(true);
    try {
      const headers = {
        Authorization: `Basic ${localStorage.getItem("authCred") || ""}`,
        "Content-Type": "application/json",
      };

      let url = `${API_BASE_URL}/tenant/?page=${page}&page_size=50`;
      if (search.trim()) {
        url += `&tenant_name=${encodeURIComponent(search.trim())}`;
      }

      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error();
      const json = await res.json();
      setTenants(json.results);
      setTotalCount(json.count);
    } catch {
      MySwal.fire("Error", "Could not load tenants", "error");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleSearch = useCallback(
    async (searchValue: string) => {
      setSearchTerm(searchValue);
      setIsSearching(true);

      setUiPage(1);
      setBatchPage(1);

      const timeoutId = setTimeout(() => {
        loadTenants(1, searchValue);
        setIsSearching(false);
      }, 2000);

      return () => clearTimeout(timeoutId);
    },
    [loadTenants]
  );

  const clearSearch = () => {
    setSearchTerm("");
    setUiPage(1);
    setBatchPage(1);
    loadTenants(1, "");
  };

  useEffect(() => {
    loadTenants(batchPage, searchTerm);
  }, [batchPage, loadTenants]);

  const inBatchPage = (uiPage - 1) % 5;

  const startIdx = inBatchPage * rowsPerPage;

  const visible = tenants.slice(startIdx, startIdx + rowsPerPage);
  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const handlePageChange = useCallback(
    (newUiPage: number) => {
      setUiPage(newUiPage);
      const newBatch = Math.floor((newUiPage - 1) / 5) + 1;
      if (newBatch !== batchPage) {
        setBatchPage(newBatch);
      }
    },
    [batchPage]
  );

  const currentUserRole = useMemo(() => {
    if (!auth?.tenants || auth.tenants.length === 0) return null;
    return auth.tenants[0]?.role_key || null;
  }, [auth?.tenants]);

  useEffect(() => {
    const newBatch = Math.floor((uiPage - 1) / 5) + 1;
    if (newBatch !== batchPage) {
      setBatchPage(newBatch);
    }
  }, [uiPage]);
  useEffect(() => {
    loadTenants(batchPage, searchTerm);
  }, [batchPage, loadTenants]);

  useEffect(() => {
    if (visible.length > 0) {
      const allSelected = visible.every((t) =>
        selectedItems.includes(t.tenant_id)
      );
      setSelectAll(allSelected && selectedItems.length > 0);
    } else {
      setSelectAll(false);
    }
  }, [visible, selectedItems]);

  const getUiPages = () => {
    const maxButtons = 3;
    let start = Math.max(1, uiPage - 1);
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) {
      start = Math.max(1, end - maxButtons + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      setSelectedItems(visible.map((t) => t.tenant_id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (tenantId: number) => {
    setSelectedItems((prev) =>
      prev.includes(tenantId)
        ? prev.filter((id) => id !== tenantId)
        : [...prev, tenantId]
    );
  };

  const handleEditServiceTaxChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    const numValue = Number(value);

    if (editTenant) {
      if (value === "" || numValue === 0) {
        setEditTenant({ ...editTenant, service_tax_default_percentage: 0 });
        setEditServiceTaxError("");
      } else if (numValue > 100) {
        setEditTenant({
          ...editTenant,
          service_tax_default_percentage: numValue,
        });
        setEditServiceTaxError("Value should not be larger than 100");
      } else {
        setEditTenant({
          ...editTenant,
          service_tax_default_percentage: numValue,
        });
        setEditServiceTaxError("");
      }
    }
  };

  const handleEditGstTaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numValue = Number(value);

    if (editTenant) {
      if (value === "" || numValue === 0) {
        setEditTenant({ ...editTenant, gst_tax_default_percentage: 0 });
        setEditGstTaxError("");
      } else if (numValue > 100) {
        setEditTenant({ ...editTenant, gst_tax_default_percentage: numValue });
        setEditGstTaxError("Value should not be larger than 100");
      } else {
        setEditTenant({ ...editTenant, gst_tax_default_percentage: numValue });
        setEditGstTaxError("");
      }
    }
  };

  const handleEditVatTaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numValue = Number(value);

    if (editTenant) {
      if (value === "" || numValue === 0) {
        setEditTenant({ ...editTenant, vat_tax_default_percentage: 0 });
        setEditVatTaxError("");
      } else if (numValue > 100) {
        setEditTenant({ ...editTenant, vat_tax_default_percentage: numValue });
        setEditVatTaxError("Value should not be larger than 100");
      } else {
        setEditTenant({ ...editTenant, vat_tax_default_percentage: numValue });
        setEditVatTaxError("");
      }
    }
  };

  const handleAddServiceTaxChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    const numValue = Number(value);

    if (value === "" || numValue === 0) {
      setAddTenant((prev) => ({ ...prev, service_tax_default_percentage: 0 }));
      setAddServiceTaxError("");
    } else if (numValue > 100) {
      setAddTenant((prev) => ({
        ...prev,
        service_tax_default_percentage: numValue,
      }));
      setAddServiceTaxError("Value should not be larger than 100");
    } else {
      setAddTenant((prev) => ({
        ...prev,
        service_tax_default_percentage: numValue,
      }));
      setAddServiceTaxError("");
    }
  };

  const handleAddGstTaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numValue = Number(value);

    if (value === "" || numValue === 0) {
      setAddTenant((prev) => ({ ...prev, gst_tax_default_percentage: 0 }));
      setAddGstTaxError("");
    } else if (numValue > 100) {
      setAddTenant((prev) => ({
        ...prev,
        gst_tax_default_percentage: numValue,
      }));
      setAddGstTaxError("Value should not be larger than 100");
    } else {
      setAddTenant((prev) => ({
        ...prev,
        gst_tax_default_percentage: numValue,
      }));
      setAddGstTaxError("");
    }
  };

  const handleAddVatTaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numValue = Number(value);

    if (value === "" || numValue === 0) {
      setAddTenant((prev) => ({ ...prev, vat_tax_default_percentage: 0 }));
      setAddVatTaxError("");
    } else if (numValue > 100) {
      setAddTenant((prev) => ({
        ...prev,
        vat_tax_default_percentage: numValue,
      }));
      setAddVatTaxError("Value should not be larger than 100");
    } else {
      setAddTenant((prev) => ({
        ...prev,
        vat_tax_default_percentage: numValue,
      }));
      setAddVatTaxError("");
    }
  };

  const openEdit = (t: Tenant) => {
    setEditTenant({ ...t });
    setEditLogoFile(null);
    setEditLogoPreview("");
    setEditServiceTaxError("");
    setEditGstTaxError("");
    setEditVatTaxError("");
    setTimeout(() => {
      const modalEl = document.getElementById("editTenantModal");
      if (modalEl) Modal.getOrCreateInstance(modalEl).show();
    }, 0);
  };

  const saveEdit = async () => {
    if (!editTenant) return;

    if (
      editServiceTaxError !== "" ||
      editGstTaxError !== "" ||
      editVatTaxError !== ""
    ) {
      MySwal.fire(
        "Error",
        "Please fix the tax percentage errors before saving.",
        "error"
      );
      return;
    }

    setIsSaving(true);

    const modalEl = document.getElementById("editTenantModal")!;
    const bsModal = Modal.getInstance(modalEl);
    if (bsModal) {
      bsModal.hide();
    }

    setTimeout(() => {
      document.querySelectorAll(".modal-backdrop").forEach((e) => e.remove());
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }, 150);
    try {
      const formData = new FormData();

      formData.append("tenant_name", editTenant.tenant_name);
      formData.append("domain_name", editTenant.domain_name);
      formData.append("is_active", String(editTenant.is_active));
      formData.append("address_line1", editTenant.address_line1 || "");
      formData.append("address_line2", editTenant.address_line2 || "");
      formData.append("city", editTenant.city || "");
      formData.append("state", editTenant.state || "");
      formData.append("country", editTenant.country || "");
      formData.append("zip_code", editTenant.zip_code || "");
      formData.append(
        "is_service_tax",
        String(editTenant.is_service_tax || false)
      );
      formData.append("is_gst_tax", String(editTenant.is_gst_tax || false));
      formData.append("is_vat_tax", String(editTenant.is_vat_tax || false));
      formData.append(
        "service_tax_default_percentage",
        String(editTenant.service_tax_default_percentage || 0)
      );
      formData.append(
        "gst_tax_default_percentage",
        String(editTenant.gst_tax_default_percentage || 0)
      );
      formData.append(
        "vat_tax_default_percentage",
        String(editTenant.vat_tax_default_percentage || 0)
      );

      if (editLogoFile) {
        formData.append("tenant_logo", editLogoFile);
      }

      const res = await fetch(
        `${API_BASE_URL}/tenant/${editTenant.tenant_id}/`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Basic ${localStorage.getItem("authCred") || ""}`,
          },
          body: formData,
        }
      );
      if (!res.ok) throw new Error();

      await MySwal.fire({
        icon: "success",
        title: "Organization updated",
        timer: 1200,
      });

      setEditTenant(null);
      setEditLogoFile(null);
      setEditLogoPreview("");
      setEditServiceTaxError("");
      setEditGstTaxError("");
      setEditVatTaxError("");
      await loadTenants(batchPage, searchTerm);
    } catch {
      MySwal.fire("Error", "Failed to save", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const deleteTenant = async (id: number) => {
    const { isConfirmed } = await MySwal.fire({
      icon: "warning",
      title: "Delete this tenant?",
      showCancelButton: true,
    });
    if (!isConfirmed) return;

    try {
      const res = await fetch(`${API_BASE_URL}/tenant/${id}/`, {
        method: "DELETE",
        headers: {
          Authorization: `Basic ${localStorage.getItem("authCred") || ""}`,
        },
      });
      if (!res.ok) throw new Error();
      MySwal.fire({ icon: "success", title: "Deleted", timer: 800 });
      await loadTenants(batchPage, searchTerm);
    } catch {
      MySwal.fire("Error", "Could not delete", "error");
    }
  };

  const handleAddTenant = async () => {
    if (
      addServiceTaxError !== "" ||
      addGstTaxError !== "" ||
      addVatTaxError !== "" ||
      addDomainError !== ""
    ) {
      MySwal.fire("Error", "Please fix the errors before saving.", "error");
      return;
    }

    setIsAdding(true);
    try {
      const formData = new FormData();

      formData.append("tenant_name", addTenant.tenant_name);
      formData.append("domain_name", addTenant.domain_name);
      formData.append("is_active", String(addTenant.is_active));
      formData.append("address_line1", addTenant.address_line1 || "");
      formData.append("address_line2", addTenant.address_line2 || "");
      formData.append("city", addTenant.city || "");
      formData.append("state", addTenant.state || "");
      formData.append("country", addTenant.country || "");
      formData.append("zip_code", addTenant.zip_code || "");
      formData.append("is_service_tax", String(addTenant.is_service_tax));
      formData.append("is_gst_tax", String(addTenant.is_gst_tax));
      formData.append("is_vat_tax", String(addTenant.is_vat_tax));
      formData.append(
        "service_tax_default_percentage",
        String(addTenant.service_tax_default_percentage)
      );
      formData.append(
        "gst_tax_default_percentage",
        String(addTenant.gst_tax_default_percentage)
      );
      formData.append(
        "vat_tax_default_percentage",
        String(addTenant.vat_tax_default_percentage)
      );

      if (addLogoFile) {
        formData.append("tenant_logo", addLogoFile);
      }

      const res = await fetch(`${API_BASE_URL}/tenant/`, {
        method: "POST",
        headers: {
          Authorization: `Basic ${localStorage.getItem("authCred") || ""}`,
        },
        body: formData,
      });
      if (!res.ok) throw new Error();
      await MySwal.fire({
        icon: "success",
        title: "Organization created",
        timer: 1200,
      });
      setShowAddModal(false);
      setAddTenant({
        tenant_name: "",
        domain_name: "",
        is_active: true,
        address_line1: "",
        address_line2: "",
        city: "",
        state: "",
        country: "",
        zip_code: "",
        is_service_tax: false,
        is_gst_tax: false,
        is_vat_tax: false,
        service_tax_default_percentage: 0,
        gst_tax_default_percentage: 0,
        vat_tax_default_percentage: 0,
      });
      setAddLogoFile(null);
      setAddLogoPreview("");
      setAddServiceTaxError("");
      setAddGstTaxError("");
      setAddVatTaxError("");
      await loadTenants(batchPage, searchTerm);
    } catch {
      MySwal.fire("Error", "Failed to add organization", "error");
    } finally {
      setIsAdding(false);
    }
  };

  if (!userRole) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading user role information...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (
    userRole.role_key !== "SUPERADMIN" &&
    userRole.role_key !== "PLATFORM_ANALYST" &&
    userRole.role_key !== "APIADMIN"
  ) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="text-center p-5">
                <div className="alert alert-danger">
                  <h4>Access Denied</h4>
                  <p>This page is only accessible to Super Administrators.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">Settings</h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              {/* /Page Header */}
              {/* Settings Menu */}
              <div className="card">
                <div className="card-body pb-0 pt-2">
                  <SettingsTabsNavigation
                    activeTab="tenant"
                    variant="bottom"
                    includeIcons={true}
                    context="tenant"
                  />
                </div>
              </div>
              {/* /Settings Menu */}
              <div className="row">
                <div className="col-xl-12 col-lg-12">
                  {/* Custom Fields */}
                  <div className="card">
                    <div className="card-body">
                      {/* Heading and Add Button Row */}
                      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap">
                        <h4 className="fw-semibold mb-0">Organizations</h4>
                        <div className="d-flex align-items-center gap-3">
                          {/* Search Input */}
                          <div className="position-relative">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search by organization name..."
                              value={searchTerm}
                              onChange={(e) => handleSearch(e.target.value)}
                              style={{ minWidth: "250px" }}
                            />
                            {isSearching && (
                              <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                                <div
                                  className="spinner-border spinner-border-sm text-primary"
                                  role="status"
                                >
                                  <span className="visually-hidden">
                                    Searching...
                                  </span>
                                </div>
                              </div>
                            )}
                            {searchTerm && !isSearching && (
                              <button
                                type="button"
                                className="btn-close position-absolute top-50 end-0 translate-middle-y me-2"
                                onClick={clearSearch}
                                style={{ fontSize: "0.75rem" }}
                                aria-label="Clear search"
                              />
                            )}
                          </div>
                          {currentUserRole !== "PLATFORM_ANALYST" && (
                            <button
                              className="btn btn-primary"
                              onClick={() => setShowAddModal(true)}
                            >
                              <i className="ti ti-square-rounded-plus me-2" />{" "}
                              Add Organization
                            </button>
                          )}
                        </div>
                      </div>
                      <TableContainer>
                        <StyledTable>
                          <thead>
                            <tr>
                              {/* <th style={{ width: 40 }}>
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={!!selectAll}
                                  onChange={handleSelectAll}
                                  aria-label="Select all tenants"
                                />
                              </th> */}
                              <th>Organization Name</th>
                              <th>Domain</th>
                              <th>Active</th>
                              {currentUserRole !== "PLATFORM_ANALYST" && (
                                <th>Actions</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {isLoading ? (
                              <tr>
                                <td colSpan={5} className="text-center py-4">
                                  <div
                                    className="spinner-border spinner-border-sm text-primary me-2"
                                    role="status"
                                  >
                                    <span className="visually-hidden">
                                      Loading...
                                    </span>
                                  </div>
                                  Loading organizations...
                                </td>
                              </tr>
                            ) : (
                              visible.map((t) => (
                                <tr key={t.tenant_id}>
                                  {/* <td>
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={selectedItems.includes(
                                        t.tenant_id
                                      )}
                                      onChange={() =>
                                        handleSelectItem(t.tenant_id)
                                      }
                                      aria-label={`Select tenant ${t.tenant_name}`}
                                    />
                                  </td> */}
                                  <td>{t.tenant_name || "—"}</td>
                                  <td>{t.domain_name || "—"}</td>
                                  <td>
                                    <span
                                      className={
                                        t.is_active
                                          ? "badge bg-success"
                                          : "badge bg-danger"
                                      }
                                    >
                                      {t.is_active ? "Active" : "Inactive"}
                                    </span>
                                  </td>
                                  {currentUserRole !== "PLATFORM_ANALYST" && (
                                    <td>
                                      <div className="dropend table-action">
                                        <Link
                                          to="#"
                                          className="action-icon"
                                          data-bs-toggle="dropdown"
                                          data-bs-flip="false"
                                        >
                                          <i className="fa fa-ellipsis-v" />
                                        </Link>
                                        <div className="dropdown-menu">
                                          <Link
                                            className="dropdown-item"
                                            to="#"
                                            onClick={() => openEdit(t)}
                                            data-bs-toggle="modal"
                                            data-bs-target="#editTenantModal"
                                          >
                                            <i className="ti ti-edit text-blue" />{" "}
                                            Edit
                                          </Link>
                                          <Link
                                            className="dropdown-item"
                                            to="#"
                                            onClick={() =>
                                              deleteTenant(t.tenant_id)
                                            }
                                          >
                                            <i className="ti ti-trash text-danger" />{" "}
                                            Delete
                                          </Link>
                                        </div>
                                      </div>
                                    </td>
                                  )}
                                </tr>
                              ))
                            )}
                            {!isLoading && visible.length === 0 && (
                              <tr>
                                <td colSpan={5} className="text-center">
                                  No organizations
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </StyledTable>
                      </TableContainer>
                      {/* pagination (same as before) */}
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <small>
                          {searchTerm ? (
                            <>
                              Found {totalCount} organization
                              {totalCount !== 1 ? "s" : ""} matching "
                              {searchTerm}"
                              {totalCount > 0 && (
                                <>
                                  {" "}
                                  • Showing{" "}
                                  {(batchPage - 1) * 50 + startIdx + 1}–
                                  {(batchPage - 1) * 50 +
                                    startIdx +
                                    visible.length}
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              Showing {(batchPage - 1) * 50 + startIdx + 1}–
                              {(batchPage - 1) * 50 + startIdx + visible.length}{" "}
                              of {totalCount}
                            </>
                          )}
                        </small>
                        <nav>
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${uiPage === 1 ? "disabled" : ""
                                }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(Math.max(1, uiPage - 1))
                                }
                              >
                                Prev
                              </button>
                            </li>
                            {getUiPages().map((p) => (
                              <li
                                key={p}
                                className={`page-item ${uiPage === p ? "active" : ""
                                  }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => handlePageChange(p)}
                                >
                                  {p}
                                </button>
                              </li>
                            ))}
                            <li
                              className={`page-item ${uiPage === totalPages ? "disabled" : ""
                                }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(
                                    Math.min(totalPages, uiPage + 1)
                                  )
                                }
                              >
                                Next
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </div>
                  {/* /Custom Fields */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}
      <div className="modal fade" id="editTenantModal">
        <div
          className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable"
          style={{ maxWidth: "95vw" }}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Edit Organization</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              />
            </div>
            <div className="modal-body">
              <ResponsiveFormSection>
                {editTenant && (
                  <>
                    <div className="mb-4">
                      <div className="profile-upload">
                        <div
                          className="profile-upload-img"
                          style={{
                            backgroundImage: `url(${editLogoPreview ||
                              editTenant.tenant_logo ||
                              CompanyLogo
                              })`,
                            width: 120,
                            height: 120,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            border:
                              editLogoPreview || editTenant.tenant_logo
                                ? "none"
                                : "2px dashed #ced4da",
                          }}
                        >
                          {!!editLogoPreview && (
                            <button
                              type="button"
                              className="profile-remove"
                              onClick={() => {
                                setEditLogoFile(null);
                                setEditLogoPreview("");
                              }}
                            >
                              <i className="feather-x" />
                            </button>
                          )}
                        </div>
                        <div className="profile-upload-content">
                          <label className="profile-upload-btn">
                            <i className="ti ti-file-broken" /> Upload Logo
                            <input
                              type="file"
                              accept="image/*"
                              className="input-img"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                setEditLogoFile(file);
                                setEditLogoPreview(URL.createObjectURL(file));
                              }}
                            />
                          </label>
                          <p>JPG, GIF or PNG. Max size of 800K</p>
                        </div>
                      </div>
                    </div>

                    <div className="form-section mb-4">
                      <h6 className="form-section-title border-bottom pb-2 mb-3">
                        <i className="ti ti-building me-2"></i>Organization
                        Details
                      </h6>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            Organization Name
                          </label>
                          <input
                            className="form-control"
                            value={editTenant.tenant_name}
                            onChange={(e) =>
                              setEditTenant((t) =>
                                t ? { ...t, tenant_name: e.target.value } : t
                              )
                            }
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            Organization Domain
                          </label>
                          <input
                            className="form-control"
                            value={editTenant.domain_name || ""}
                            onChange={(e) =>
                              setEditTenant((t) =>
                                t ? { ...t, domain_name: e.target.value } : t
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-section mb-4">
                      <h6 className="form-section-title border-bottom pb-2 mb-3">
                        <i className="ti ti-map-pin me-2"></i>Address
                        Information
                      </h6>
                      <div className="mb-3">
                        <label className="form-label">Address Line 1</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editTenant.address_line1 || ""}
                          onChange={(e) =>
                            setEditTenant((t) =>
                              t ? { ...t, address_line1: e.target.value } : t
                            )
                          }
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Address Line 2</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editTenant.address_line2 || ""}
                          onChange={(e) =>
                            setEditTenant((t) =>
                              t ? { ...t, address_line2: e.target.value } : t
                            )
                          }
                        />
                      </div>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">City</label>
                          <input
                            type="text"
                            className="form-control"
                            value={editTenant.city || ""}
                            onChange={(e) =>
                              setEditTenant((t) =>
                                t ? { ...t, city: e.target.value } : t
                              )
                            }
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">State</label>
                          <input
                            type="text"
                            className="form-control"
                            value={editTenant.state || ""}
                            onChange={(e) =>
                              setEditTenant((t) =>
                                t ? { ...t, state: e.target.value } : t
                              )
                            }
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Country</label>
                          <input
                            type="text"
                            className="form-control"
                            value={editTenant.country || ""}
                            onChange={(e) =>
                              setEditTenant((t) =>
                                t ? { ...t, country: e.target.value } : t
                              )
                            }
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">ZIP Code</label>
                          <input
                            type="text"
                            className="form-control"
                            value={editTenant.zip_code || ""}
                            onChange={(e) =>
                              setEditTenant((t) =>
                                t ? { ...t, zip_code: e.target.value } : t
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Status</label>
                      <div>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="tenantStatus"
                            id="tenantActiveRadio"
                            checked={!!editTenant.is_active}
                            onChange={() =>
                              setEditTenant((t) =>
                                t ? { ...t, is_active: true } : t
                              )
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor="tenantActiveRadio"
                          >
                            Active
                          </label>
                        </div>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="tenantStatus"
                            id="tenantInactiveRadio"
                            checked={!!!editTenant.is_active}
                            onChange={() =>
                              setEditTenant((t) =>
                                t ? { ...t, is_active: false } : t
                              )
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor="tenantInactiveRadio"
                          >
                            Inactive
                          </label>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </ResponsiveFormSection>
            </div>
            <div className="modal-footer">
              <button className="btn btn-light" data-bs-dismiss="modal">
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={saveEdit}
                disabled={
                  isSaving ||
                  !!editServiceTaxError ||
                  !!editGstTaxError ||
                  !!editVatTaxError
                }
              >
                {isSaving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Add Tenant Modal */}
      <div
        className={`modal fade${showAddModal ? " show d-block" : ""}`}
        id="addTenantModal"
        tabIndex={-1}
        style={showAddModal ? { background: "rgba(0,0,0,0.3)" } : {}}
      >
        <div
          className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable"
          style={{ maxWidth: "95vw" }}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Add Organization</h5>
              <button
                type="button"
                className="btn-close custom-btn-close border p-1 me-0 text-dark"
                onClick={() => {
                  setShowAddModal(false);
                  setAddLogoFile(null);
                  setAddLogoPreview("");
                  setAddServiceTaxError("");
                  setAddGstTaxError("");
                  setAddVatTaxError("");
                }}
                aria-label="Close"
                data-bs-dismiss="close"
              >
                {" "}
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body">
              <ResponsiveFormSection>
                <div className="mb-4">
                  <div className="profile-upload">
                    <div
                      className="profile-upload-img"
                      style={{
                        backgroundImage: `url(${addLogoPreview || CompanyLogo
                          })`,
                        width: 120,
                        height: 120,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        border: addLogoPreview ? "none" : "2px dashed #ced4da",
                      }}
                    >
                      {!!addLogoPreview && (
                        <button
                          type="button"
                          className="profile-remove"
                          onClick={() => {
                            setAddLogoFile(null);
                            setAddLogoPreview("");
                          }}
                        >
                          <i className="feather-x" />
                        </button>
                      )}
                    </div>
                    <div className="profile-upload-content">
                      <label className="profile-upload-btn">
                        <i className="ti ti-file-broken" /> Upload Logo
                        <input
                          type="file"
                          accept="image/*"
                          className="input-img"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setAddLogoFile(file);
                            setAddLogoPreview(URL.createObjectURL(file));
                          }}
                        />
                      </label>
                      <p>JPG, GIF or PNG. Max size of 800K</p>
                    </div>
                  </div>
                </div>

                <div className="form-section mb-4">
                  <h6 className="form-section-title border-bottom pb-2 mb-3">
                    <i className="ti ti-building me-2"></i>Organization Details
                  </h6>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Organization Name</label>
                      <input
                        className="form-control"
                        value={addTenant.tenant_name}
                        onChange={(e) =>
                          setAddTenant((t) => ({
                            ...t,
                            tenant_name: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Organization Domain</label>
                      <input
                        className={`form-control${addDomainError ? " is-invalid" : ""
                          }`}
                        value={addTenant.domain_name}
                        onChange={(e) => {
                          setAddTenant((t) => ({
                            ...t,
                            domain_name: e.target.value,
                          }));
                          const val = e.target.value.trim();
                          if (val && !val.includes(".")) {
                            setAddDomainError(
                              "Please enter a valid domain (e.g. example.com)"
                            );
                          } else {
                            setAddDomainError("");
                          }
                        }}
                      />
                      {addDomainError && (
                        <div className="invalid-feedback d-block">
                          {addDomainError}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-section mb-4">
                  <h6 className="form-section-title border-bottom pb-2 mb-3">
                    <i className="ti ti-map-pin me-2"></i>Address Information
                  </h6>
                  <div className="mb-3">
                    <label className="form-label">Address Line 1</label>
                    <input
                      type="text"
                      className="form-control"
                      value={addTenant.address_line1}
                      onChange={(e) =>
                        setAddTenant((t) => ({
                          ...t,
                          address_line1: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Address Line 2</label>
                    <input
                      type="text"
                      className="form-control"
                      value={addTenant.address_line2}
                      onChange={(e) =>
                        setAddTenant((t) => ({
                          ...t,
                          address_line2: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">City</label>
                      <input
                        type="text"
                        className="form-control"
                        value={addTenant.city}
                        onChange={(e) =>
                          setAddTenant((t) => ({ ...t, city: e.target.value }))
                        }
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">State</label>
                      <input
                        type="text"
                        className="form-control"
                        value={addTenant.state}
                        onChange={(e) =>
                          setAddTenant((t) => ({ ...t, state: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Country</label>
                      <input
                        type="text"
                        className="form-control"
                        value={addTenant.country}
                        onChange={(e) =>
                          setAddTenant((t) => ({
                            ...t,
                            country: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">ZIP Code</label>
                      <input
                        type="text"
                        className="form-control"
                        value={addTenant.zip_code}
                        onChange={(e) =>
                          setAddTenant((t) => ({
                            ...t,
                            zip_code: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="addTenantStatus"
                        id="addTenantActiveRadio"
                        checked={!!addTenant.is_active}
                        onChange={() =>
                          setAddTenant((t) => ({ ...t, is_active: true }))
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor="addTenantActiveRadio"
                      >
                        Active
                      </label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="addTenantStatus"
                        id="addTenantInactiveRadio"
                        checked={!!!addTenant.is_active}
                        onChange={() =>
                          setAddTenant((t) => ({ ...t, is_active: false }))
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor="addTenantInactiveRadio"
                      >
                        Inactive
                      </label>
                    </div>
                  </div>
                </div>
              </ResponsiveFormSection>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-light"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddTenant}
                disabled={
                  isAdding ||
                  !addTenant.tenant_name ||
                  !addTenant.domain_name ||
                  !!addDomainError ||
                  !!addServiceTaxError ||
                  !!addGstTaxError ||
                  !!addVatTaxError
                }
              >
                {isAdding ? "Adding…" : "Add Organization"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
